// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import SEO from "components/SEO";

// SEO structured data
import {
  graph,
  lodgingSchema,
  organizationSchema,
  websiteSchema,
  buildBreadcrumbSchema,
} from "config/structuredData";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Home page sections
import PropertyHighlights from "pages/Home/sections/PropertyHighlights";
import VanPromo from "pages/Home/sections/VanPromo";
import PromoBanner from "pages/Home/sections/PromoBanner";
import PhotoGalleryPreview from "pages/Home/sections/PhotoGalleryPreview";
import Amenities from "pages/Home/sections/Amenities";
import Reviews from "pages/Home/sections/Reviews";
import CategoryRatings from "pages/Home/sections/CategoryRatings";
import LocationInfo from "pages/Home/sections/LocationInfo";
import BookingCTA from "pages/Home/sections/BookingCTA";

// Data
import propertyData from "pages/Home/sections/data/propertyData";
import photosData from "pages/Home/sections/data/photosData";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Use the pool photo as the hero background
const heroPhoto = photosData.property.photos.find((p) => p.hero);

const listingLinks = [
  { name: "Airbnb", href: "https://www.airbnb.com/rooms/9803481", color: "#FF5A5F" },
  { name: "Vrbo", href: "https://www.vrbo.com/4631717", color: "#3B5998" },
];

// Above this width the van card floats up into the stats section and overlaps the hero,
// sitting to the right of the guest-rating counter. Below it there isn't room to do that
// without covering the counter, so the card drops in as a normal in-flow stats block.
const DESKTOP_XL = "@media (min-width:1800px)";

function Home() {
  return (
    <>
      <SEO
        path="/"
        imageAlt="Beachfront pool and ocean view at Stalley Beach House, Costa Rica"
        schema={graph(
          lodgingSchema,
          organizationSchema,
          websiteSchema,
          buildBreadcrumbSchema([{ name: "Home", path: "/" }])
        )}
      />
      <PromoBanner />
      <DefaultNavbar routes={routes} sticky externalLinks={listingLinks} />
      <MKBox
        minHeight="75vh"
        width="100%"
        position="relative"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.1),
              rgba(gradients.dark.state, 0.5)
            )}, url(${heroPhoto.src})`,
          backgroundSize: "cover",
          backgroundPosition: "50% 65%",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid container justifyContent="center">
            {/* Property intro */}
            <Grid item xs={12} sx={{ maxWidth: "760px", marginX: "auto", textAlign: "center" }}>
              <MKTypography
                variant="h1"
                color="white"
                sx={({ breakpoints }) => ({
                  // Elegant script brand font for the hero title. Great Vibes only ships a 400
                  // weight, so keep fontWeight regular (no faux-bold) and lean on size + shadow
                  // for presence over the photo. Sizes are bumped vs a sans face because scripts
                  // read visually smaller.
                  fontFamily: "'Great Vibes', cursive",
                  fontWeight: 400,
                  lineHeight: 1.1,
                  textShadow: "0 3px 20px rgba(0, 0, 0, 0.45)",
                  fontSize: "4.25rem",
                  [breakpoints.down("md")]: {
                    fontSize: "3rem",
                  },
                  // Fluid growth on large / ultra-wide screens so the title keeps pace
                  // with the full-bleed hero background instead of looking small on 2K/4K.
                  "@media (min-width:1600px)": {
                    fontSize: "clamp(4.75rem, 1rem + 4vw, 7.5rem)",
                  },
                })}
              >
                {propertyData.name}
              </MKTypography>
              <MKTypography
                variant="body1"
                color="white"
                opacity={0.8}
                mt={1}
                mb={1}
                sx={{
                  "@media (min-width:1600px)": {
                    fontSize: "clamp(1.25rem, 0.45rem + 0.8vw, 1.75rem)",
                  },
                }}
              >
                {propertyData.tagline} &middot; {propertyData.location.country}
              </MKTypography>
              <MKBox display="flex" flexWrap="wrap" justifyContent="center" gap={1.5} mt={2} mb={3}>
                {[
                  { icon: "savings", text: "Save 15% to 20% vs Airbnb" },
                  { icon: "money_off", text: "No booking fees" },
                  { icon: "verified", text: "Book direct with owner" },
                ].map((badge) => (
                  <MKBox
                    key={badge.text}
                    display="flex"
                    alignItems="center"
                    gap={0.8}
                    px={2}
                    py={0.8}
                    borderRadius="lg"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      backdropFilter: "blur(8px)",
                      border: "2px solid rgba(255, 255, 255, 0.53)",
                      boxShadow: "0 0 12px 2px rgba(46, 156, 215, 0)",
                    }}
                  >
                    <Icon sx={{ color: "#fff", fontSize: "1.2rem" }}>{badge.icon}</Icon>
                    <MKTypography variant="button" sx={{ color: "#fff", fontWeight: 600 }}>
                      {badge.text}
                    </MKTypography>
                  </MKBox>
                ))}
              </MKBox>
              <MKButton variant="gradient" color="info" size="large" component="a" href="/booking">
                <Icon sx={{ mr: 1 }}>calendar_month</Icon>
                Book Your Stay
              </MKButton>
            </Grid>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          position: "relative",
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* Van promo - full desktop: sits to the right of the stats, lifted up onto the hero */}
        <MKBox
          sx={{
            display: "none",
            [DESKTOP_XL]: { display: "block" },
            position: "absolute",
            top: 0,
            // Anchor to the viewport centre (left:50%), then translate right so the card sits
            // centred in the gap between the guest-rating counter (~477px right of centre) and
            // the right edge of the screen. The 25vw term tracks half that growing gap, so on
            // wide / high-res monitors the card drifts toward the middle of the free space on
            // the right instead of hugging the counter.
            left: "50%",
            transform: "translate(calc(25vw + 48.5px), -52%)",
            width: 380,
            zIndex: 20,
          }}
        >
          <VanPromo />
        </MKBox>
        <PropertyHighlights />
        {/* Van promo - default: an in-flow promo block placed right after the stats
            (hero -> stats -> van promo -> gallery) until the screen is wide enough for the
            card to overlap the hero. Horizontal (image left, copy right) on tablet+ so the
            wider card fills its space. */}
        <MKBox
          sx={{
            display: "block",
            [DESKTOP_XL]: { display: "none" },
            maxWidth: 680,
            mx: "auto",
            my: 2,
          }}
        >
          <VanPromo horizontal />
        </MKBox>
        <PhotoGalleryPreview />
        <Amenities />
        <Reviews />
        <CategoryRatings />
        <LocationInfo />
        <BookingCTA />
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Home;
